import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "./styles.css";

const FormSelect = ({
    label,
    name,
    value,
    onChange,
    options = [],
    placeholder = "Selecciona una opción",
    error = "",
    required = false,
    className = "",
    width = "100%",
    searchable = false,
    isLoading = false,
    actionButton,
    multiple = false,
    disabled = false,
    badge
}) => {
    const [internalValue, setInternalValue] = useState(multiple ? value ?? [] : value ?? "");
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [menuStyles, setMenuStyles] = useState({});
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);

    const selectedOption = multiple ? null : options.find(opt => String(opt.value) === String(internalValue));

    const filteredOptions = searchable
        ? options.filter(opt => opt.label?.toLowerCase().includes(searchTerm?.toLowerCase()))
        : options;

    const handleSelect = (val) => {
        if (disabled) return;

        if (multiple) {
            let newValues = [...internalValue];
            if (newValues.includes(val)) {
                newValues = newValues.filter(v => v !== val);
            } else {
                newValues.push(val);
            }
            setInternalValue(newValues);
            onChange?.({ target: { name, value: newValues } });
        } else {
            setInternalValue(val);
            setIsOpen(false);
            onChange?.({ target: { name, value: val } });
        }
        setSearchTerm("");
    };

    const handleSelectAll = () => {
        if (disabled) return;
        if (internalValue.length === options.length) {
            setInternalValue([]);
            onChange?.({ target: { name, value: [] } });
        } else {
            const allValues = options.map(opt => opt.value);
            setInternalValue(allValues);
            onChange?.({ target: { name, value: allValues } });
        }
    };

    useEffect(() => {
        if (value !== undefined) setInternalValue(value);
    }, [value, multiple]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!dropdownRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const updatePosition = () => {
            if (isOpen && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                setMenuStyles({
                    position: "absolute",
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                    zIndex: 9999,
                });
            }
        };
        updatePosition();
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isOpen]);

    const displayLabel = multiple
        ? options.filter(opt => internalValue.includes(opt.value)).map(opt => opt.label).join(", ")
        : selectedOption?.label || "";

    return (
        <div
            className={`form-field custom-form-field ${className} ${disabled ? "disabled" : ""}`}
            style={{ width }}
        >
            {label && (
                <label htmlFor={name} className="form-label form-field-label mb-0 ms-3">
                    {label}
                    {required && <span className="form-field-label ms-1">*</span>}
                    {badge && (
                        <span className="form-field-badge ms-1">{badge}</span>
                    )}
                </label>
            )}

            <div className="d-flex flex-row align-items-center" ref={dropdownRef}>
                <div className={`w-100 ${searchable && "position-relative"}`} ref={triggerRef}>
                    {searchable ? (
                        <>
                            <input
                                type="text"
                                className={`form-field-input w-100 pe-4 text-truncate ${
                                    error ? "is-invalid" : ""
                                } ${isLoading ? "loading-skeleton" : ""}`}
                                placeholder={placeholder}
                                value={isOpen ? searchTerm : displayLabel}
                                onClick={() => !isLoading && !disabled && setIsOpen(true)}
                                onChange={(e) => {
                                    if (disabled) return;
                                    setSearchTerm(e.target.value);
                                    setIsOpen(true);
                                }}
                                disabled={isLoading || disabled}
                                required={required && (!internalValue || (multiple && internalValue.length === 0))}
                            />
                            {!isLoading && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none" className="position-absolute form-field-input-svg">
                                    <path d="M7.54236 9.08938C7.14647 9.08938 6.75052 8.93837 6.4484 8.63626L0.45311 2.64096C-0.151037 2.03681 -0.151037 1.05726 0.45311 0.453101C1.05723 -0.151034 2.03682 -0.151034 2.64095 0.453101L7.54236 5.35457L12.4438 0.453101C13.0479 -0.151034 14.0275 -0.151034 14.6316 0.453101C15.2358 1.05726 15.2358 2.03681 14.6316 2.64096L8.63633 8.63626C8.33423 8.93837 7.93825 9.08938 7.54236 9.08938Z" fill="#9DACBD"/>
                                </svg>
                            )}
                        </>
                    ) : (
                        <button
                            className={`form-field-input btn w-100 d-flex justify-content-between align-items-center ${
                                error ? "is-invalid" : ""
                            } ${isLoading ? "loading-skeleton" : ""}`}
                            type="button"
                            onClick={() => !isLoading && !disabled && setIsOpen(!isOpen)}
                            disabled={isLoading || disabled}
                        >
                            {selectedOption ? selectedOption.label : placeholder}
                            {!isLoading && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none">
                                    <path
                                        d="M7.54236 9.08938C7.14647 9.08938 6.75052 8.93837 6.4484 8.63626L0.45311 2.64096C-0.151037 2.03681 -0.151037 1.05726 0.45311 0.453101C1.05723 -0.151034 2.03682 -0.151034 2.64095 0.453101L7.54236 5.35457L12.4438 0.453101C13.0479 -0.151034 14.0275 -0.151034 14.6316 0.453101C15.2358 1.05726 15.2358 2.03681 14.6316 2.64096L8.63633 8.63626C8.33423 8.93837 7.93825 9.08938 7.54236 9.08938Z"
                                        fill="#9DACBD"
                                    />
                                </svg>
                            )}
                        </button>
                    )}
                </div>

                {actionButton && !isLoading && !disabled && (
                    <div>
                        <svg
                            className="cursor-pointer"
                            onClick={() => {
                                actionButton();
                                setIsOpen(false);
                            }}
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3ZM12 6C11.4477 6 11 6.44772 11 7V11H7C6.44772 11 6 11.4477 6 12C6 12.5523 6.44772 13 7 13H11V17C11 17.5523 11.4477 18 12 18C12.5523 18 13 17.5523 13 17V13H17C17.5523 13 18 12.5523 18 12C18 11.4477 17.5523 11 17 11H13V7C13 6.44772 12.5523 6 12 6Z"
                                fill="#6FB75D"
                            />
                        </svg>
                    </div>
                )}
            </div>

            {isOpen && !disabled &&
                createPortal(
                    <div ref={menuRef} className="dropdown-menu show p-0 pe-1 form-select-portal" style={menuStyles}>
                        <div className="pe-2 dropdown-menu-scroll">
                            {multiple && (
                                <div className="dropdown-item py-1 my-1 text-truncate" onClick={handleSelectAll} title="Seleccionar todo">
                                    <input type="checkbox" checked={internalValue.length === options.length} readOnly className="me-2" />
                                    Seleccionar todo
                                </div>
                            )}

                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((opt, idx) => (
                                    <div
                                        key={idx}
                                        className="dropdown-item py-1 my-1 text-truncate"
                                        onClick={() => handleSelect(opt.value)}
                                        title={opt.label}
                                    >
                                        {multiple && <input type="checkbox" checked={internalValue.includes(opt.value)} readOnly className="me-2" />}
                                        {opt.label}
                                    </div>
                                ))
                            ) : (
                                <div className="dropdown-item py-1 my-1">No hay resultados</div>
                            )}
                        </div>
                    </div>,
                    document.body
                )}

            <input type="hidden" name={name} value={multiple ? internalValue.join(",") : internalValue} required={required} />
            {error && <div className="invalid-feedback form-field-error">{error}</div>}
        </div>
    );
};

export default FormSelect;
